import { CSSProperties } from 'react';
import clsx from 'clsx';
import styles from './meter.module.scss';
import { MeterLayout, MeterTone } from './meter.types';

interface MeterProps {
  value: number;
  layout?: MeterLayout;
  tone?: MeterTone;
  label?: string;
  /** Overrides the accessible name when the visible label leans on a glyph. */
  ariaLabel?: string;
  caption?: string;
  count?: string;
  /** Row-layout label column, in px. The default 52px suits a rating scale, not prose. */
  labelWidth?: number;
  /** Row-layout count column, in px. The default 40px fits three digits. */
  countWidth?: number;
  className?: string;
}

export default function Meter({
  value,
  layout = 'labelled',
  tone = 'brand',
  label,
  ariaLabel,
  caption,
  count,
  labelWidth,
  countWidth,
  className,
}: MeterProps) {
  // Clamps the range AND the domain. A caller computing `resolved / total * 100`
  // with total 0 hands us Infinity, which would otherwise clamp to a full bar
  // reading "100% resolved" for a topic that had no calls at all.
  const percent = Number.isFinite(value) ? Math.min(100, Math.max(0, Math.round(value))) : 0;

  // A progressbar must be named. The inline layout is the decorative upload bar
  // and never has a label; the other layouts only take the role once one is
  // given, since an unnamed progressbar is worse than none.
  const isMeaningful = layout !== 'inline' && Boolean(label);

  const track = (
    <div
      aria-label={isMeaningful ? (ariaLabel ?? label) : undefined}
      aria-valuemax={isMeaningful ? 100 : undefined}
      aria-valuemin={isMeaningful ? 0 : undefined}
      aria-valuenow={isMeaningful ? percent : undefined}
      className={styles.track}
      role={isMeaningful ? 'progressbar' : undefined}
    >
      <div className={clsx(styles.fill, styles[`fill-${tone}`])} style={{ width: `${percent}%` }} />
    </div>
  );

  if (layout === 'labelled') {
    return (
      <div className={clsx(styles['meter-labelled'], className)}>
        {(label || caption) && (
          <div className={styles.head}>
            <b className={styles.label}>{label}</b>
            <span className={styles.caption}>{caption}</span>
          </div>
        )}
        {track}
      </div>
    );
  }

  if (layout === 'row') {
    return (
      <div
        className={clsx(styles['meter-row'], className)}
        style={
          {
            ...(labelWidth && { '--meter-label-width': `${labelWidth}px` }),
            ...(countWidth && { '--meter-count-width': `${countWidth}px` }),
          } as CSSProperties
        }
      >
        <span className={styles.label}>{label}</span>
        {track}
        <span className={styles.count}>{count}</span>
      </div>
    );
  }

  return <div className={clsx(styles['meter-inline'], className)}>{track}</div>;
}
